import { AddOutlined, DeleteOutline, PlusOne } from "@mui/icons-material";
import {
  Alert,
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Grid2 as Grid,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router";
import { useParams } from "react-router-dom";
import IntlMessages from "../../components/IntlMessages/IntlMessages";
import { PageHeader } from "../../components/PageHeader";
import { Select } from "../../components/Select";
import { TextField } from "../../components/TextField";
import { useAuthContext } from "../../contexts/authentication/AuthenticationContext";
import { useSchemasContext } from "../../contexts/schemas/SchemasContext";
import { useSchemasActions } from "../../contexts/schemas/UseSchemasActions";
import {
  SchemaAttribute,
  SchemaAttributes,
  SchemaCreateData,
  SchemaUpdateData,
} from "../../core/models/Schema";
import {
  ATTRIBUTE_TYPES_OPTIONS,
  AttributeType,
  MAX_LENGTH_PREVIEW_ATTRIBUTES,
} from "../../defines/attribute-type";
import { notify } from "../../libs/observables/notification";
import { useCategoriesActions } from "../../contexts/categories/UseCategoriesActions";
import { useCategoriesContext } from "../../contexts/categories/CategoriesContext";

import { UNSelect } from "un-react-ui-components";
import { InfoBox } from "../../components/InfoBox";
import { Switch } from "../../components/Switch";
import _ from "lodash";
import {
  capitalizeFirstLetter,
  sortAttributesByPreview,
} from "../../util/utils";
import { IconsModal } from "./iconsModal.container";
import { GRAY_BLACK } from "../../themes/colors";
import { useIconsContext } from "../../contexts/icons/IconsContext";
import { useIconsActions } from "../../contexts/icons/UseIconsActions";
import SchemaFormSkeleton from "./schema.skeleton.container";
enum Attributes {
  Add,
  Change,
  Sort,
  Delete,
}

const SchemaNewEdit = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const params = useParams<{ id: string }>();
  const { userInfo } = useAuthContext();
  const { selectedSchema, schemasError, schemasLoading } = useSchemasContext();
  const { selectedIcon, iconsResponseData } = useIconsContext();
  const { categoriesResponseData } = useCategoriesContext();
  const {
    dispatchGetSchema,
    dispatchCreateSchema,
    dispatchPatchSchema,
    dispatchPutSchema,
    dispatchCleanSchemasContext,
    setSchemasLoading,
  } = useSchemasActions();
  const { dispatchGetCategories, dispatchCleanCategoriesContext } =
    useCategoriesActions();
  const { dispatchGetIcons, setSelectedIcon, dispatchCleanIconsContext } =
    useIconsActions();
  const previewAttributes = [
    selectedSchema?.display_attribute,
    selectedSchema?.display_attribute_optional,
  ].filter(Boolean);

  const initialValues = {
    name: selectedSchema?.name || "",
    version: selectedSchema?.version || "1.0",
    attributes:
      sortAttributesByPreview(
        selectedSchema?.attributes || [
          {
            name: "",
            type: AttributeType.Text,
          },
        ],
        previewAttributes.filter((attr): attr is string => !!attr)
      ) || [],
    previewAttributes: previewAttributes,
    selectedCategory: selectedSchema?.category?.toString(),
    icon: selectedSchema?.icon,
  };

  const [formData, setFormData] = useState(initialValues);
  const [loading, setLoading] = useState<boolean>(false);

  const [isDisabledSaveButton, setIsDisabledSaveButton] = useState(true);
  const [openIconsModal, setOpenIconsModal] = useState(false);

  //#region Load and cleanup
  useEffect(() => {
    if (params.id) {
      setSchemasLoading(true);
      dispatchGetSchema(+params.id);
    }
    dispatchGetCategories();
    dispatchGetIcons();

    return () => {
      dispatchCleanSchemasContext();
      dispatchCleanCategoriesContext();
      dispatchCleanIconsContext();
    };
  }, []);

  useEffect(() => {
    if (!selectedSchema) return;

    // Notify on schema creation
    if (!params.id && !schemasError) {
      notify("success")({ message: "Schema created!" });
      navigate(-1);
      return;
    }

    if (schemasError) return;

    // Extract and filter preview attributes
    const previewAttribs = [
      selectedSchema?.display_attribute,
      selectedSchema?.display_attribute_optional,
    ].filter((attr): attr is string => !!attr);

    // Set form data
    setFormData({
      name: selectedSchema.name || "",
      version: selectedSchema.version || "1.0",
      attributes:
        sortAttributesByPreview(selectedSchema.attributes, previewAttribs) ||
        [],
      previewAttributes: previewAttribs,
      selectedCategory: selectedSchema.category?.toString(),
      icon: selectedSchema?.icon,
    });
    // Notify on schema update
    if (loading) {
      notify("success")({ message: "Schema updated!" });
    }

    setLoading(false);
    setSchemasLoading(false);
  }, [selectedSchema, categoriesResponseData, schemasError]);

  useEffect(() => {
    if (!iconsResponseData || !selectedSchema?.icon) return;

    const selectedIcon = iconsResponseData.find(
      (item) => item.id === selectedSchema.icon
    );

    if (selectedIcon) {
      setSelectedIcon(selectedIcon);
    }
  }, [iconsResponseData, selectedSchema]);

  useEffect(() => {
    setIsDisabledSaveButton(_.isEqual(formData, initialValues));
  }, [formData, initialValues]);
  //#endregion

  //#region Page actions
  const onCancel = () => navigate(-1);

  const onSave = () => {
    if (!formData.name) {
      notify("error")({ message: "Schema name is required!" });
      return;
    }
    if (!formData.attributes.length) {
      notify("error")({ message: "Schema attributes is required!" });
      return;
    }
    if (!formData.version) {
      console.warn("[NewSchema][Save] Version is not defined.");
      return;
    }
    if (!userInfo?.organization) {
      console.warn("[NewSchema][Save] User organization is not defined.");
      return;
    }
    setLoading(true);

    const schema: SchemaCreateData = {
      name: formData.name,
      organization_name: userInfo.organization,
      category: Number(formData.selectedCategory),
      display_attribute:
        formData.previewAttributes?.[0] ?? (null as string | null),
      display_attribute_optional:
        formData.previewAttributes?.[1] ?? (null as string | null),
      icon: formData?.icon || 0,
      schema_json: {
        schema_name: formData.name,
        attributes: formData.attributes.reduce(
          (obj, attr) => {
            if (attr.name) obj[attr.name] = attr.type;
            return obj;
          },
          {} as Record<string, AttributeType>
        ),
      },
    };
    if (!params.id) {
      dispatchCreateSchema(schema);
    } else {
      const schemaModified: SchemaUpdateData = {
        id: +params.id,
        ...schema,
      };
      const previewAttributes = [
        selectedSchema?.display_attribute,
        selectedSchema?.display_attribute_optional,
      ].filter(Boolean);
      if (
        selectedSchema?.name !== formData.name ||
        !_.isEqual(
          sortAttributesByPreview(
            selectedSchema.attributes,
            previewAttributes.filter((attr): attr is string => !!attr)
          ),
          formData.attributes
        )
      ) {
        dispatchPutSchema(schemaModified);
      } else {
        const schemaUpdated: SchemaUpdateData = {
          id: +params.id,
          category: Number(formData.selectedCategory),
          icon: formData?.icon || 0,
          display_attribute:
            formData.previewAttributes?.[0] ?? (null as string | null),
          display_attribute_optional:
            formData.previewAttributes?.[1] ?? (null as string | null),
        };
        dispatchPatchSchema(schemaUpdated);
      }
    }
  };
  //#endregion

  //#region Attributes
  const handleAttributesChanges = (args: AttributesChangesProps) => {
    let newAttributes: SchemaAttributes;
    const { action } = args;

    switch (action) {
      case Attributes.Add:
        newAttributes = [
          { name: "", type: AttributeType.Text },
          ...formData.attributes,
        ];
        handleOnChangeFormData("attributes")(newAttributes);
        break;
      case Attributes.Change: {
        const { index: attrToEdit, newAttr } = args;
        newAttributes = [...formData.attributes];
        newAttributes.splice(attrToEdit, 1, newAttr);
        handleOnChangeFormData("attributes")(newAttributes);
        break;
      }
      case Attributes.Sort: {
        const { newAttr } = args;
        let updatedPreview;
        let prev = formData.previewAttributes;
        if (prev.includes(newAttr.name)) {
          updatedPreview = prev.filter((name) => name !== newAttr.name);
        } else {
          if (prev.length < MAX_LENGTH_PREVIEW_ATTRIBUTES) {
            updatedPreview = [...prev, newAttr.name];
          } else {
            updatedPreview = [prev[1], newAttr.name]; // Replace the first one
          }
        }
        newAttributes = formData.attributes;
        handleOnChangeFormData("previewAttributes")(updatedPreview);
        handleOnChangeFormData("attributes")(newAttributes);
        break;
      }
      case Attributes.Delete: {
        const { index: attrToDelete } = args;
        newAttributes = [...formData.attributes];
        newAttributes.splice(attrToDelete, 1);
        handleOnChangeFormData("attributes")(newAttributes);
        break;
      }
    }
  };
  //#endregion

  //#region Categories
  const handleCategorySelection = (value: string | undefined) => {
    const selected =
      categoriesResponseData?.find((cat) => cat.value === value) || undefined;
    handleOnChangeFormData("selectedCategory")(selected?.value);
  };
  //#endregion

  const handleOnChangeFormData = (id: string) => (value: any) => {
    setFormData((prev) => ({
      ...prev,
      [id]: value,
    }));
  };
  return (
    <>
      <Backdrop open={loading}>
        <CircularProgress data-testid="loader" />
      </Backdrop>
      <Box
        data-testid="schema"
        className="min-w-full space-y-[1.875rem] flex-box"
      >
        <PageHeader
          titleTranslationKey={params.id ? "schema.details" : "schema.new"}
          tooltip="schemas"
          divider
          disableSave={isDisabledSaveButton}
          containerClass="flex-initial"
          onCancel={onCancel}
          onSave={onSave}
        />
        {/* Form */}
        {!schemasLoading ? (
          <Grid container columnSpacing={4}>
            {/* Left panel */}
            <Grid
              container
              direction="column"
              size={{ xs: 6 }}
              className="space-y-[1.875rem]"
            >
              <TextField
                id="schema.name"
                label="schema.name"
                placeholder="schema.name.placeholder"
                value={formData.name}
                onChange={handleOnChangeFormData("name")}
                disabled={!!selectedSchema}
              />
              <Grid container direction="column" className="space-y-[0.5rem]">
                <Typography data-testid="categories.label" variant="body2">
                  <IntlMessages id={"categories.label"} />
                </Typography>
                <UNSelect
                  id={"schema.category"}
                  placeholderTranslationKey="categories.placeholder"
                  labelTranslationKey={undefined}
                  options={categoriesResponseData || []}
                  value={formData.selectedCategory}
                  onChange={(value) => {
                    handleCategorySelection(value as string | undefined);
                  }}
                />
              </Grid>
              <Grid direction="column" container>
                <Typography data-testid="icon.label" variant="body2">
                  <IntlMessages id={"schema.icon.label"} />
                </Typography>
                {!formData.icon ? (
                  <Button
                    data-testid="schema.icon.add"
                    variant="text"
                    className="font-medium mt-2"
                    startIcon={<AddOutlined />}
                    onClick={() => {
                      setOpenIconsModal(true);
                    }}
                    sx={{
                      flex: 1,
                      justifyContent: "flex-start",
                    }}
                    color="primary"
                  >
                    <IntlMessages id="schema.icon.add" />
                  </Button>
                ) : (
                  <Button
                    data-testid="schema.icon.add"
                    variant="text"
                    className="font-medium mt-2"
                    onClick={() => {
                      setOpenIconsModal(true);
                    }}
                    color="primary"
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flex: 1,
                        gap: 1,
                        padding: 2,
                        borderRadius: 2,
                        cursor: "pointer",
                        border: "1px solid #C8C8C8",
                      }}
                      bgcolor={"white"}
                    >
                      <Box
                        sx={{
                          width: 24,
                          height: 24,
                          backgroundImage: `url(${selectedIcon?.icon})`,
                          backgroundSize: "cover",
                          backgroundPosition: "center",
                        }}
                      />
                      <Typography variant="body1" color={GRAY_BLACK}>
                        {capitalizeFirstLetter(
                          selectedIcon?.name || ""
                        ).replaceAll("_", " ")}
                      </Typography>
                    </Box>
                  </Button>
                )}
              </Grid>
              <TextField
                id="schema.version"
                label="schema.version"
                value={formData.version}
                onChange={handleOnChangeFormData("version")}
                disabled
              />
              {selectedSchema && (
                <>
                  <TextField
                    id="schema.id"
                    label="schema.schemaId"
                    value={selectedSchema.schemaId}
                    onChange={() => {}}
                    copy
                    disabled
                  />
                  <TextField
                    id="schema.enabled"
                    label="schema.enabled"
                    value={intl.formatMessage({
                      id: `schema.enabled.${
                        selectedSchema.enabled ? "valid" : "deprecated"
                      }`,
                    })}
                    onChange={() => {}}
                    disabled
                  />
                </>
              )}
            </Grid>
            {/* Right panel */}
            <Grid size={{ xs: 6 }}>
              <Box className="h-full rounded-[10px] bg-gray-input p-[1.875rem] space-y-[1.25rem]">
                <Box className="flex justify-between items-center">
                  <Typography variant="body2">
                    <IntlMessages id="schema.attributes" />
                  </Typography>
                  <Button
                    data-testid="schema.attributes.add"
                    variant="text"
                    className="font-medium"
                    startIcon={<AddOutlined />}
                    onClick={() =>
                      handleAttributesChanges({ action: Attributes.Add })
                    }
                  >
                    <IntlMessages id="add" />
                  </Button>
                </Box>
                <InfoBox
                  text={intl.formatMessage({
                    id: "schema.attribute.hint.preview",
                  })}
                />
                {formData.attributes.length > 0 ? (
                  formData.attributes.map((attr, index) => (
                    <Attribute
                      key={index}
                      attr={attr}
                      index={index}
                      onChangeAttributes={handleAttributesChanges}
                      previewAttributes={formData.previewAttributes.filter(
                        (attr): attr is string => !!attr
                      )}
                    />
                  ))
                ) : (
                  <Alert variant="outlined" severity="error">
                    <IntlMessages id="schema.attributes.disclaimer" />
                  </Alert>
                )}
              </Box>
            </Grid>
          </Grid>
        ) : (
          <SchemaFormSkeleton />
        )}
      </Box>
      {openIconsModal && (
        <IconsModal
          open={openIconsModal}
          onClose={() => setOpenIconsModal(false)}
          onSave={(id: number) => {
            handleOnChangeFormData("icon")(id);
            setOpenIconsModal(false);
          }}
        />
      )}
    </>
  );
};

type AttributesChangesProps =
  | {
      action: Attributes.Add;
    }
  | {
      action: Attributes.Change;
      index: number;
      newAttr: SchemaAttribute;
    }
  | {
      action: Attributes.Sort;
      index: number;
      newAttr: SchemaAttribute;
    }
  | {
      action: Attributes.Delete;
      index: number;
    };

const Attribute = ({
  attr,
  index,
  previewAttributes,
  onChangeAttributes,
}: {
  attr: SchemaAttribute;
  previewAttributes: Array<string>;
  index: number;
  onChangeAttributes(args: AttributesChangesProps): void;
}) => {
  const { name, type } = attr;
  const intl = useIntl();
  const indexString = String(index + 1).padStart(2, "0");
  return (
    <Box
      data-testid={`schema.attribute.${index}`}
      className="rounded-2xl bg-white p-6 space-y-4"
    >
      <Box className="flex justify-between items-center">
        <Typography variant="body2" fontWeight={500}>
          {`${intl.formatMessage({ id: "schema.attribute" })} ${indexString}`}
        </Typography>
        {previewAttributes.length < MAX_LENGTH_PREVIEW_ATTRIBUTES ||
        previewAttributes.includes(name) ? (
          <Switch
            id={"schema.attribute.preview" + index}
            label="schema.attribute.preview"
            value={previewAttributes.includes(name)}
            onChange={() => {
              onChangeAttributes({
                action: Attributes.Sort,
                index,
                newAttr: attr,
              });
            }}
          />
        ) : (
          <Typography variant="body2" fontWeight={500}>
            {`${MAX_LENGTH_PREVIEW_ATTRIBUTES} ${intl.formatMessage({ id: "schema.attribute.max.preview" })}`}
          </Typography>
        )}
      </Box>
      <TextField
        id={`schema.attribute.${index}.name`}
        placeholder="schema.attribute.name.placeholder"
        value={name}
        onChange={(newName) =>
          onChangeAttributes({
            action: Attributes.Change,
            index,
            newAttr: { name: newName, type },
          })
        }
      />
      <Select<AttributeType>
        id={`schema.attribute.${index}.type`}
        options={ATTRIBUTE_TYPES_OPTIONS}
        value={type}
        onChange={(newType) =>
          onChangeAttributes({
            action: Attributes.Change,
            index,
            newAttr: { name, type: newType as AttributeType },
          })
        }
      />
      <Box className="w-full flex justify-end">
        <Button
          data-testid={`schema.attribute.${index}.delete`}
          variant="text"
          color="error"
          startIcon={<DeleteOutline />}
          onClick={() =>
            onChangeAttributes({ action: Attributes.Delete, index })
          }
        >
          <Typography variant="body2" fontWeight={500}>
            <IntlMessages id="schema.attribute.delete" />
          </Typography>
        </Button>
      </Box>
    </Box>
  );
};

export { SchemaNewEdit };
